import { create } from "zustand";
import { devtools } from "zustand/middleware";

type Theme = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

interface ThemeState {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  initialize: () => void;
  updateResolvedTheme: () => void;
}

const THEME_STORAGE_KEY = "theme-preference";
const DARK_CLASS = "dark";

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyThemeClass(theme: ResolvedTheme): void {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add(DARK_CLASS);
  } else {
    root.classList.remove(DARK_CLASS);
  }
}

function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === "system") {
    return getSystemTheme();
  }
  return theme;
}

export const useThemeStore = create<ThemeState>()(
  devtools(
    (set, get) => {
      let mediaQuery: MediaQueryList | null = null;

      if (typeof window !== "undefined") {
        mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        mediaQuery.addEventListener("change", () => {
          const state = get();
          if (state.theme === "system") {
            state.updateResolvedTheme();
          }
        });
      }

      return {
        theme: "system",
        resolvedTheme: getSystemTheme(),

        updateResolvedTheme: () => {
          const { theme } = get();
          const resolved = resolveTheme(theme);
          set({ resolvedTheme: resolved });
          applyThemeClass(resolved);
        },

        setTheme: (newTheme: Theme) => {
          localStorage.setItem(THEME_STORAGE_KEY, newTheme);
          const resolved = resolveTheme(newTheme);
          set({ theme: newTheme, resolvedTheme: resolved });
          applyThemeClass(resolved);
        },

        toggleTheme: () => {
          const { resolvedTheme } = get();
          const newTheme = resolvedTheme === "dark" ? "light" : "dark";
          localStorage.setItem(THEME_STORAGE_KEY, newTheme);
          const resolved = resolveTheme(newTheme);
          set({ theme: newTheme, resolvedTheme: resolved });
          applyThemeClass(resolved);
        },

        initialize: () => {
          const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;

          if (storedTheme && ["light", "dark", "system"].includes(storedTheme)) {
            set({ theme: storedTheme });
          } else {
            const systemTheme = getSystemTheme();
            localStorage.setItem(THEME_STORAGE_KEY, systemTheme);
            set({ theme: systemTheme });
          }

          const { theme } = get();
          const resolved = resolveTheme(theme);
          set({ resolvedTheme: resolved });
          applyThemeClass(resolved);
        },
      };
    },
    {
      name: "ThemeStore",
      enabled: import.meta.env.DEV,
    }
  )
);

export const useTheme = () => useThemeStore((state) => state.theme);
export const useResolvedTheme = () => useThemeStore((state) => state.resolvedTheme);
export const useSetTheme = () => useThemeStore((state) => state.setTheme);
export const useToggleTheme = () => useThemeStore((state) => state.toggleTheme);
export const useInitializeTheme = () => useThemeStore((state) => state.initialize);

export const useThemeActions = () => ({
  setTheme: useThemeStore((state) => state.setTheme),
  toggleTheme: useThemeStore((state) => state.toggleTheme),
});
